import {
  Button,
  List,
  ListItem,
  Stack,
  Typography,
  ListItemText,
} from '@mui/material';
import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import FireIcon from 'assets/membership-features/logo.svg?react';
import {
  MEMBERSHIP_PLANS,
  MEMBERSHIP_PLANS_AMOUNTS,
  MembershipPlan,
} from '../../membership.constants';
import { useMembershipPlan } from '../MembershipPlanProvider';
import { FeatureItem, FeatureList } from './FeatureList';
import { useSupportUrlState } from '../../hooks/useSupportUrlState';

type ExpandedContentProps = {
  plan?: MembershipPlan | 'donate' | 'teams';
};

type PlanButtonProps = {
  plan: MembershipPlan;
  selected: boolean;
  onSelect: () => void;
};

const useStyles = makeStyles()((theme) => ({
  membershipButton: {
    flex: 1,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    justifyContent: 'flex-start',
    borderWidth: 2,
    borderColor: theme.palette.divider,
  },
  selectedMembershipButton: {
    borderColor: theme.palette.brand[500],
  },
  list: {
    '& li': {
      padding: 0,
      lineHeight: 1,
    },
    '& li::before': {
      content: '"•"',
      display: 'inline-block',
      width: theme.spacing(2),
    },
  },
}));

const PlanButton = (props: PlanButtonProps): JSX.Element => {
  const { plan, selected, onSelect } = props;
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  return (
    <Button
      variant="outlined"
      color="inherit"
      className={cx(
        classes.membershipButton,
        selected && classes.selectedMembershipButton,
      )}
      onClick={() => onSelect()}
    >
      <Stack spacing={1} sx={{ height: 1, width: 1 }}>
        <Stack spacing={1} sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            component="p"
            fontWeight="fontWeightMedium"
            sx={{ textTransform: 'uppercase' }}
          >
            {t(
              plan === MEMBERSHIP_PLANS.proYearly
                ? 'supportUs.proMembership.plans.yearly.title'
                : 'supportUs.proMembership.plans.monthly.title',
            )}
          </Typography>
        </Stack>

        <Typography variant="h1" component="p">
          <Trans
            i18nKey={
              plan === MEMBERSHIP_PLANS.proYearly
                ? 'supportUs.proMembership.plans.yearly.price'
                : 'supportUs.proMembership.plans.monthly.price'
            }
            values={{ price: MEMBERSHIP_PLANS_AMOUNTS[plan] }}
          >
            <Typography
              variant="h3"
              component="span"
              fontWeight={500}
              sx={{ verticalAlign: 'top' }}
            >
              $
            </Typography>
            99.99
          </Trans>
        </Typography>

        <Stack sx={{ alignItems: 'center' }}>
          <List dense disablePadding className={classes.list}>
            <ListItem disableGutters>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {t(
                      plan === MEMBERSHIP_PLANS.proYearly
                        ? 'supportUs.proMembership.plans.billedAnnually'
                        : 'supportUs.proMembership.plans.billedMonthly',
                    )}
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
            </ListItem>

            <ListItem disableGutters>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {t('supportUs.proMembership.plans.cancelAnytime')}
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
            </ListItem>
          </List>
        </Stack>
      </Stack>
    </Button>
  );
};

export const ExpandedContent = (props: ExpandedContentProps): JSX.Element => {
  const { plan } = props;
  const { t } = useTranslation();
  const { continueOnWeb, verificationError, getNextLink, handleNext } =
    useMembershipPlan();
  const { plan: selectedPlan, source, setPlan } = useSupportUrlState();
  const posthog = usePostHog();

  const captureEventInPosthog = useCallback(
    (clickedPlan: string) => {
      posthog.capture('support_us_click_through', {
        plan: clickedPlan,
        source,
      });
    },
    [posthog, source],
  );

  if (
    plan === MEMBERSHIP_PLANS.proYearly ||
    plan === MEMBERSHIP_PLANS.proMonthly
  ) {
    const membershipProLink = getNextLink(selectedPlan as MembershipPlan);

    const getButtonText = (): string => {
      if (selectedPlan === MEMBERSHIP_PLANS.proMonthly) {
        return t('supportUs.proMembership.plans.monthly.button', {
          price: MEMBERSHIP_PLANS_AMOUNTS[MEMBERSHIP_PLANS.proMonthly],
        });
      }
      return t('supportUs.proMembership.plans.yearly.button', {
        price: MEMBERSHIP_PLANS_AMOUNTS[MEMBERSHIP_PLANS.proYearly],
      });
    };

    return (
      <>
        <FeatureList plan={plan} />

        <Stack direction="row" spacing={1.5}>
          <PlanButton
            plan={MEMBERSHIP_PLANS.proYearly}
            selected={selectedPlan === MEMBERSHIP_PLANS.proYearly}
            onSelect={() => setPlan(MEMBERSHIP_PLANS.proYearly)}
          />
          <PlanButton
            plan={MEMBERSHIP_PLANS.proMonthly}
            selected={selectedPlan === MEMBERSHIP_PLANS.proMonthly}
            onSelect={() => setPlan(MEMBERSHIP_PLANS.proMonthly)}
          />
        </Stack>

        <Button
          fullWidth
          color="primary"
          size="large"
          component={Link}
          to={
            continueOnWeb ? { pathname: membershipProLink } : membershipProLink
          }
          target={continueOnWeb ? '_blank' : undefined}
          disabled={
            Boolean(verificationError) ||
            (selectedPlan !== MEMBERSHIP_PLANS.proYearly &&
              selectedPlan !== MEMBERSHIP_PLANS.proMonthly)
          }
          onClick={() => {
            captureEventInPosthog(selectedPlan as MembershipPlan);
            handleNext(selectedPlan as MembershipPlan);
          }}
        >
          {getButtonText()}
        </Button>
      </>
    );
  }

  if (plan === MEMBERSHIP_PLANS.yearly) {
    const membershipLink = getNextLink(MEMBERSHIP_PLANS.yearly);
    const price = MEMBERSHIP_PLANS_AMOUNTS[MEMBERSHIP_PLANS.yearly];
    return (
      <>
        <FeatureList plan={plan} />

        <Button
          fullWidth
          color="primary"
          size="large"
          component={Link}
          to={continueOnWeb ? { pathname: membershipLink } : membershipLink}
          target={continueOnWeb ? '_blank' : undefined}
          disabled={Boolean(verificationError)}
          onClick={() => {
            captureEventInPosthog(MEMBERSHIP_PLANS.yearly);
            handleNext(MEMBERSHIP_PLANS.yearly);
          }}
        >
          {t('supportUs.membership.button', { price })}
        </Button>
      </>
    );
  }

  if (plan === 'teams') {
    return (
      <>
        <FeatureList plan="teams" />

        <Button
          fullWidth
          color="primary"
          size="large"
          component={Link}
          to="/organization/purchase/"
          onClick={() => captureEventInPosthog('teams')}
        >
          {t('supportUs.teams.ctaButton')}
        </Button>
      </>
    );
  }

  return (
    <>
      <FeatureItem label={t('supportUs.donate.purpose')} Icon={FireIcon} />

      <Button
        fullWidth
        color="primary"
        size="large"
        component={Link}
        to="/donate"
        onClick={() => captureEventInPosthog('donate')}
      >
        {t('supportUs.donate.button')}
      </Button>
    </>
  );
};
